import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import type {
  AnnotationDraft,
  Comment,
  CommentStatus,
  WorkspaceMember,
} from "../api/types";
import { ApiError } from "../api/types";
import { CommentComposer } from "../components/review/CommentComposer";
import { ReviewRail } from "../components/review/ReviewRail";
import { useAuthOptional } from "../auth/AuthContext";
import { DoczCanvas } from "../components/canvas/DoczCanvas";
import { useReadCanvasSurface } from "../components/canvas/useReadCanvasSurface";
import { EvidencePanel } from "../components/EvidencePanel";
import { StatusBadge } from "../components/StatusBadge";
import { DownloadMenu } from "../components/DownloadMenu";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { VersionHistoryPanel } from "../components/VersionHistoryPanel";
import { HashChip } from "../compose/controls";
import { ArrowLeft, BookOpen, Trash2 } from "../compose/Icon";
import { useToast } from "../compose/Toast";
import { EmptyState, ErrorState, Spinner } from "../compose/states";
import { formatDate } from "../lib/format";
import { mergeSurfaces } from "../lib/reviewOrder";
import { useAsync } from "../lib/useAsync";
import type { ScrollIntent } from "../lib/scrollMotion";
import { useCitationDeepLink } from "../lib/useCitationDeepLink";
import { useWorkspaceStream } from "../realtime/useWorkspaceStream";
import {
  applyReactionEcho,
  reduceCommentsForDoc,
  toggleOwnReaction,
} from "../realtime/reconcile";
import { getRestOutbox } from "../sync/restOutbox";
import { mintTempId } from "../sync/tempIds";

interface PopoverState {
  x: number;
  y: number;
  draft: AnnotationDraft;
}

/**
 * Document viewer `/w/:slug/doc/:id` (design screens §3) + the full review layer
 * (brief §6/§7). The imperative canvas + anchor engine now lives in the shared
 * `useDoczCanvas` hook + `<DoczCanvas>` (design §2.1 "the load-bearing
 * refactor"), so this legacy single-resource viewer and the new
 * `<ArticleWorkbench>` paint through ONE implementation. Read behaviour is
 * byte-for-byte identical to before the extraction.
 *
 *   · select text → durable selector → the CommentComposer popover → POST an
 *     anchored root comment.
 *   · on load → GET comments → the hook re-anchors each ROOT into a
 *     `docz-comment` highlight span + renders the threaded rail. Click a thread →
 *     the hook scrolls to + blooms its anchored span.
 */
export function DocumentViewer() {
  const { slug = "", id = "" } = useParams<{ slug: string; id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuthOptional();
  const user = auth?.user ?? null;
  // Delete/archive is staff-only, mirroring the server gate (global staff OR a
  // workspace `staff`-role member). On success we leave for the library, where
  // the document is now gone.
  const canManage =
    !!user?.is_staff ||
    (auth?.memberships ?? []).some(
      (m) => m.workspace_slug === slug && m.role === "staff",
    );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const archiveDoc = useCallback(async () => {
    setDeleting(true);
    try {
      await api.archiveDocument(id);
      toast.show("Document deleted");
      navigate(`/w/${slug}/library`);
    } catch (err) {
      toast.show(
        err instanceof ApiError
          ? err.message || "Couldn't delete the document."
          : "Couldn't delete the document.",
      );
      setDeleting(false);
    }
  }, [id, slug, navigate, toast]);
  const { data: doc, loading, error } = useAsync(() => api.getDocument(id), [id]);

  const [comments, setComments] = useState<Comment[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [popover, setPopover] = useState<PopoverState | null>(null);
  // rich-comments §13: the comment whose card must lift (a doc highlight hovered).
  const [linkedCommentId, setLinkedCommentId] = useState<number | null>(null);

  const onSelectText = useCallback(
    (draft: AnnotationDraft | null, rect: DOMRect | null) => {
      if (!draft || !rect) {
        setPopover(null);
        return;
      }
      setPopover({ x: rect.left + rect.width / 2, y: rect.bottom + 8, draft });
    },
    [],
  );

  // THE READ SURFACE — canvas only. `useDoczCanvas` (which painted
  // `doc.rendered_html` into a `.ela-doc` div and re-anchored by walking DOM
  // text nodes) is deleted; `<ReadCanvas>` resolves anchors from the docz blocks
  // and this hook holds the two maps the review rail renders from.
  const {
    ref: readCanvasRef,
    anchorStates,
    anchorPoints,
    onAnchorStates,
    onAnchorPoints,
    focusAnchor,
    setPendingType,
    clearPending,
    setLinkedHover,
  } = useReadCanvasSurface();

  // The review rail's document order. ONE surface here, so the merge is a
  // one-element list — but it is the SAME `mergeSurfaces` the two-pane workbench
  // gutter uses, so neither column can grow its own idea of reading order.
  const documentOrder = useMemo(
    () => ({
      points: mergeSurfaces([{ kind: "article", points: anchorPoints }]),
      states: anchorStates,
    }),
    [anchorPoints, anchorStates],
  );

  const loadComments = useCallback(async () => {
    try {
      setComments(await api.listComments(id));
    } catch {
      // A read failure leaves the doc readable; the rail simply stays empty.
    }
  }, [id]);

  const loadMembers = useCallback(async () => {
    try {
      setMembers(await api.listMembers(id));
    } catch {
      // Mentions degrade gracefully to an empty member list.
    }
  }, [id]);

  // Load the review data once the doc lands (the hook owns painting + anchoring).
  useEffect(() => {
    if (!doc) return;
    void loadComments();
    void loadMembers();
  }, [doc, loadComments, loadMembers]);

  const cancelPopover = useCallback(() => {
    setPopover(null);
    clearPending();
    window.getSelection()?.removeAllRanges();
  }, [clearPending]);

  const createRoot = useCallback(
    async (draft: {
      body: string;
      body_format: string;
      type: Comment["type"];
      mentions: number[];
      visibility?: Comment["visibility"];
      shared_with?: number[];
    }) => {
      if (!popover) return;
      try {
        await api.createComment(id, {
          ...popover.draft,
          body: draft.body,
          body_format: draft.body_format,
          type: draft.type,
          mentions: draft.mentions,
          visibility: draft.visibility,
          shared_with: draft.shared_with,
        });
        setPopover(null);
        clearPending();
        window.getSelection()?.removeAllRanges();
        await loadComments();
        toast.show("Comment added");
      } catch (err) {
        // ONLINE failure → surface the specific reason (author/validation). OFFLINE
        // or a transient network drop → queue it in the outbox (replays on
        // reconnect via the same POST) instead of a hard "couldn't save".
        const offline =
          !getRestOutbox().isOnline || (err instanceof ApiError && err.status === 0);
        if (offline) {
          const tempId = mintTempId();
          void getRestOutbox().enqueueCreateRoot(tempId, Number(id), {
            ...popover.draft,
            body: draft.body,
            body_format: draft.body_format,
            type: draft.type,
            mentions: draft.mentions,
            visibility: draft.visibility,
            shared_with: draft.shared_with,
          });
          setPopover(null);
          clearPending();
          window.getSelection()?.removeAllRanges();
          toast.show("You're offline — comment queued, will sync");
        } else {
          // Keep the pending highlight + popover so the reviewer can retry (§3.2).
          toast.show(
            err instanceof ApiError && err.message
              ? err.message
              : "Couldn't save the comment",
          );
        }
      }
    },
    [popover, id, clearPending, toast],
  );

  const onReply = useCallback(
    async (parentId: number, draft: { body: string; body_format: string; mentions: number[] }) => {
      try {
        await api.replyToComment(parentId, draft);
        await loadComments();
        toast.show("Reply posted");
      } catch {
        toast.show("Couldn't post the reply");
      }
    },
    [loadComments, toast],
  );

  const onStatus = useCallback(
    async (commentId: number, status: CommentStatus) => {
      try {
        await api.setCommentStatus(commentId, status);
        await loadComments();
      } catch {
        toast.show("Couldn't update status");
      }
    },
    [loadComments, toast],
  );

  // comment-visibility (§6A.7): change a thread root's scope via the card pill.
  const onVisibility = useCallback(
    async (
      commentId: number,
      body: import("../api/types").VisibilityChange,
    ) => {
      try {
        await api.setCommentVisibility(commentId, body);
        await loadComments();
        toast.show("Visibility updated");
      } catch {
        toast.show("Couldn't update visibility");
      }
    },
    [loadComments, toast],
  );

  // comment-delete: the author deletes their own note. Deliberately NOT
  // optimistic — the outcome is not knowable client-side. The server TOMBSTONES:
  // a note with no replies vanishes from the next read, one with living replies
  // returns as a content-free placeholder holding the thread together. Guessing
  // which happened would show the wrong thing half the time, so the authoritative
  // reload IS the reconcile.
  const onDelete = useCallback(
    async (commentId: number) => {
      try {
        await api.deleteComment(commentId);
        await loadComments();
      } catch (err) {
        toast.show(
          err instanceof ApiError && err.status === 403
            ? "Only the author can delete this comment"
            : "Couldn't delete that comment",
        );
      }
    },
    [loadComments, toast],
  );

  // review-notes §3: author edits their own note body. Optimistic — patch the
  // local body immediately, persist through the edit endpoint, reconcile with the
  // server echo; on failure reload to roll the optimistic patch back.
  const onEdit = useCallback(
    async (commentId: number, draft: { body: string; body_format: string }) => {
      // Optimistic body patch, then route through the REST-replay outbox: online it
      // PATCHes immediately (with a one-shot CSRF-token refresh on a stale-token 403
      // — the ROOT CAUSE of the reported online "couldn't save"); offline it QUEUES
      // + replays on reconnect. The reconcile effect folds the server echo / rolls
      // back on a genuine (author/validation) rejection.
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? { ...c, body: draft.body, body_format: draft.body_format }
            : c,
        ),
      );
      void getRestOutbox().enqueueEdit(commentId, draft.body, draft.body_format);
      if (!getRestOutbox().isOnline) {
        toast.show("You're offline — edit queued, will sync");
      }
    },
    [toast],
  );

  // review-notes §4: toggle the viewer's emoji reaction. Optimistic — patch the
  // viewer's own flag + count immediately, persist through the toggle endpoint,
  // reconcile with the authoritative server echo; on failure reload to roll back.
  const onToggleReaction = useCallback(
    (commentId: number, emoji: string, add: boolean) => {
      if (!user) return;
      const viewer = { id: Number(user.id), name: user.name };
      // Optimistic chip flip, then route through the REST-replay outbox: applies
      // now when online, QUEUES when offline (NO hard "couldn't update reaction").
      // The reconcile effect folds the authoritative echo and rolls back only on a
      // genuine server rejection.
      setComments((prev) =>
        toggleOwnReaction(prev, commentId, emoji, add, viewer),
      );
      void getRestOutbox().enqueueReaction(Number(user.id), commentId, emoji, add);
    },
    [user],
  );

  // Fold REST-outbox reconciliations (server truth on drain) back into state:
  // reaction echoes, edited bodies, and created comments — plus rollback +
  // a specific message on a genuine rejection. Optimistic patches already showed
  // the change; this settles it against the server.
  useEffect(() => {
    const off = getRestOutbox().subscribe((e) => {
      if (e.table === "reactions") {
        if (e.kind === "applied") {
          setComments((prev) => applyReactionEcho(prev, e.commentId, e.echo));
        } else {
          void loadComments();
          toast.show(e.message || "Couldn't update reaction");
        }
      } else if (e.table === "comment_edit") {
        if (e.kind === "applied") {
          setComments((prev) =>
            prev.map((c) =>
              c.id === e.commentId
                ? {
                    ...c,
                    body: e.row.body,
                    body_format: e.row.body_format,
                    updated_at: e.row.updated_at,
                  }
                : c,
            ),
          );
        } else {
          void loadComments();
          toast.show(e.message || "Couldn't save the edit");
        }
      } else if (e.table === "comment_create") {
        // Applied → the real comment now exists server-side; a reload folds it in
        // (and drops any optimistic placeholder). Rejected → reload + reason.
        void loadComments();
        if (e.kind === "rejected") {
          toast.show(e.message || "Couldn't save the comment");
        }
      }
    });
    return off;
  }, [loadComments, toast]);

  // Default `navigate`: the reviewer clicked a note card's anchor button and is
  // watching. The citation deep-link below passes `"restore"` — it is a landing.
  const onFocusAnchor = useCallback(
    (commentId: number, intent: ScrollIntent = "navigate") => {
      if (focusAnchor(commentId, intent) === null) {
        toast.show("This comment's text is no longer in the document");
      }
    },
    [focusAnchor, toast],
  );

  // review-notes §2.3: the deep-link target (a `?reply=`/`?comment=`/`?c=` id)
  // whose thread must auto-expand so the reveal lands even though threads collapse
  // by default.
  const revealCommentId = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const raw = p.get("reply") ?? p.get("comment") ?? p.get("c");
    const n = raw ? Number(raw) : Number.NaN;
    return Number.isFinite(n) ? n : null;
  }, [location.search]);

  // Real-time (realtime-sse): subscribe to the workspace live stream and
  // reconcile incoming comment/reply/status events into the doc's comment set
  // (dedupe by id). The hook's re-anchor effect then re-locates any new root.
  useWorkspaceStream(slug, (event) => {
    setComments((prev) => reduceCommentsForDoc(prev, Number(id), event));
  });

  // Permalink deep-link (permalink-citations): a `?comment=<id>` (or the
  // notification bell's `?c=<id>`) query resolves the citation, then scrolls to
  // + blooms the cited span once the canvas has painted its highlight spans.
  const citationSearch = useMemo(() => {
    const p = new URLSearchParams(location.search);
    if (p.has("c") && !p.has("comment")) p.set("comment", p.get("c") ?? "");
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  }, [location.search]);

  useCitationDeepLink({
    docId: id,
    search: citationSearch,
    ready: !loading && !!doc && comments.length > 0,
    // A permalink LANDING — instant, not animated (see the option's docs).
    reveal: (rootId) => focusAnchor(rootId, "restore"),
    // A permalink LANDING — instant, matching `scrollIntoCenter`'s own intent.
    onResolved: (res) => onFocusAnchor(res.anchor.root_id, "restore"),
    onError: () => toast.show("That citation link is no longer available"),
  });

  return (
    <div>
      <div className="docview__bar">
        <Link to={`/w/${slug}/library`} className="btn btn--ghost" data-testid="back-to-library" style={{ padding: "6px 10px" }}>
          <ArrowLeft size={16} strokeWidth={1.5} />
          Library
        </Link>
        <h1 className="docview__bar-title" data-testid="doc-title">
          {doc?.title ?? "Document"}
        </h1>
        <div className="docview__bar-spacer" />
        {doc ? <StatusBadge status={doc.status} /> : null}
        {doc?.evidence ? (
          <HashChip hash={doc.evidence.hash} onCopy={() => toast.show("Hash copied")} />
        ) : null}
        {doc ? (
          <DownloadMenu
            docId={id}
            title={doc.title}
            // The SERVER's decision — so a blocked/forbidden export is a
            // disabled item naming the reason, never an `<a download>` that
            // saves the 403 JSON body to disk as `Report.pdf`.
            gate={doc.export_gate}
            onRefusal={(message) => toast.show(message)}
          />
        ) : null}
        {doc && canManage ? (
          <button
            type="button"
            className="btn btn--ghost"
            style={{ padding: "6px 10px", color: "var(--norkron-state-danger)" }}
            aria-label="Delete document"
            title="Delete document"
            data-testid="doc-delete"
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 size={16} strokeWidth={1.5} />
          </button>
        ) : null}
      </div>

      {loading ? (
        <div style={{ paddingTop: 80 }}>
          <Spinner label="Opening document…" />
        </div>
      ) : error ? (
        <div className="page">
          <ErrorState
            title="Couldn't open this document"
            body="It may have been moved or you may not have access."
            action={
              <Link to={`/w/${slug}`} className="btn btn--secondary">
                Back to library
              </Link>
            }
          />
        </div>
      ) : doc ? (
        <div className="docview__body">
          <DoczCanvas
            surfaceClassName="ela-canvas docview__canvas"
            testId="doc-canvas"
            readDocId={id}
            readComments={comments}
            readTitle={doc.title}
            readLang={doc.lang}
            readCanvasRef={readCanvasRef}
            onReadCanvasSelect={onSelectText}
            onReadCanvasAnchorStates={onAnchorStates}
            onReadCanvasAnchorPoints={onAnchorPoints}
            onReadCanvasHoverComment={setLinkedCommentId}
          />

          <aside className="docview__rail">
            <VersionHistoryPanel docId={id} />
            <div className="rail__card">
              <h3 className="rail__h">Document</h3>
              <div className="meta__row">
                <span className="meta__k">Status</span>
                <span className="meta__v">
                  <StatusBadge status={doc.status} />
                </span>
              </div>
              <div className="meta__row">
                <span className="meta__k">Brand</span>
                <span className="meta__v">{doc.brand}</span>
              </div>
              <div className="meta__row">
                <span className="meta__k">Category</span>
                <span className="meta__v">{doc.category}</span>
              </div>
              <div className="meta__row">
                <span className="meta__k">Language</span>
                <span className="meta__v">{doc.lang.toUpperCase()}</span>
              </div>
              <div className="meta__row">
                <span className="meta__k">Format</span>
                <span className="meta__v">{doc.format}</span>
              </div>
              <div className="meta__row">
                <span className="meta__k">Folder</span>
                <span className="meta__v">{doc.folder}</span>
              </div>
              <div className="meta__row">
                <span className="meta__k">Delivered</span>
                <span className="meta__v">{formatDate(doc.delivered_at)}</span>
              </div>
            </div>

            <ReviewRail
              comments={comments}
              members={members}
              anchorStates={anchorStates}
              documentOrder={documentOrder}
              onReply={onReply}
              onStatus={onStatus}
              onFocusAnchor={onFocusAnchor}
              slug={slug}
              docId={id}
              onCopyLink={() => toast.show("Link copied")}
              linkedCommentId={linkedCommentId}
              onHoverCard={setLinkedHover}
              viewerId={user ? Number(user.id) : undefined}
              viewerIsStaff={user?.is_staff}
              onVisibility={onVisibility}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleReaction={onToggleReaction}
              revealCommentId={revealCommentId}
            />

            {doc.evidence ? (
              <EvidencePanel evidence={doc.evidence} />
            ) : (
              <div className="rail__card">
                <h3 className="rail__h">Delivery evidence</h3>
                <EmptyState
                  icon={<BookOpen size={26} strokeWidth={1.5} />}
                  title="Not yet delivered"
                  body="Evidence appears once every required reviewer passes on the head content hash."
                />
              </div>
            )}
          </aside>
        </div>
      ) : null}

      {popover ? (
        <div className="anno-popover anno-popover--composer" data-testid="annotation-popover" style={{ left: popover.x, top: popover.y }}>
          <CommentComposer
            members={members}
            autoFocus
            submitLabel="Comment"
            onCancel={cancelPopover}
            onSubmit={createRoot}
            onTypeChange={setPendingType}
          />
        </div>
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title="Delete document"
          body={`“${doc?.title ?? "This document"}” will be removed from the library. Staff can restore it.`}
          confirmLabel="Delete document"
          danger
          busy={deleting}
          onConfirm={archiveDoc}
          onCancel={() => (deleting ? undefined : setConfirmDelete(false))}
          testid="delete-doc-dialog"
        />
      ) : null}
    </div>
  );
}
